import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "../router";
import { useState } from "react";
import { useData } from "../context/DataContext";
import { openContactModal } from "../utils/ui";

// Icon mapper
const getIcon = (platform: string) => {
    switch (platform) {
        case 'github': return <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg>;
        case 'steam': return <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><title>Steam icon</title><path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" /></svg>;
        case 'contact': return <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 2v.511l-8 6.223-8-6.222V6h16zM4 18V9.044l7.386 5.745a.994.994 0 0 0 1.228 0L20 9.044 20.002 18H4z"></path></svg>;
        case 'instagram': return <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M7.75 2h8.5C19.55 2 22 4.45 22 7.75v8.5c0 3.3-2.45 5.75-5.75 5.75h-8.5C4.45 22 2 19.55 2 16.25v-8.5C2 4.45 4.45 2 7.75 2zm0 1.5c-2.48 0-4.25 1.77-4.25 4.25v8.5c0 2.48 1.77 4.25 4.25 4.25h8.5c2.48 0 4.25-1.77 4.25-4.25v-8.5c0-2.48-1.77-4.25-4.25-4.25h-8.5zm8.5 2.25a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z" /></svg>;
        default: return null;
    }
}

// Hover Outline Colors
const getHoverClass = (platform: string) => {
    switch (platform) {
        case 'github': return "hover:border-[#fff] hover:text-[#fff]";
        case 'steam': return "hover:border-[#66c0f4] hover:text-[#66c0f4]";
        case 'contact': return "hover:border-emerald-500 hover:text-emerald-500";
        case 'instagram': return "hover:border-[#E1306C] hover:text-[#E1306C]";
        default: return "hover:border-white hover:text-white";
    }
}

export default function Home() {
    const radiusClass = "rounded-3xl";
    const borderClass = "border-2 border-[#2A2A2A]"; // Hover handled via specific classes

    const { navigate } = useRouter();

    const { data: globalData } = useData();
    const data = globalData.home;

    // Loading handled via !data check in JSX

    // Loading check handled inside JSX for smoother transition
    // if (!data) return null; // Removed

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.04,
                delayChildren: 0
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        show: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.4,
                ease: "backOut"
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="min-h-screen flex flex-col justify-center items-center py-20 px-4 md:py-10 pb-32"
        >

            {!data ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mx-auto h-auto">

                    {/* Hero Skeleton (2 cols) */}
                    <div className="col-span-1 md:col-span-2 min-h-[360px] bg-[#111] rounded-3xl border border-[#222] animate-pulse relative p-10 flex flex-col items-center justify-center gap-6">
                        {/* Decorative corners */}
                        <div className="absolute top-6 left-6 w-16 h-3 bg-[#222] rounded opacity-20" />
                        <div className="absolute bottom-6 right-6 w-16 h-3 bg-[#222] rounded opacity-20" />

                        <div className="w-48 h-24 bg-[#222] rounded-2xl opacity-50" /> {/* Title */}
                        <div className="w-32 h-4 bg-[#222] rounded-full opacity-40" /> {/* Subtitle */}
                        <div className="w-64 h-12 bg-[#222] rounded-xl opacity-30" /> {/* Desc */}
                    </div>

                    {/* Right Col Skeleton */}
                    <div className="col-span-1 flex flex-col gap-4 h-full">
                        {/* Teaser */}
                        <div className="h-[150px] bg-[#111] rounded-3xl border border-[#222] animate-pulse p-6 flex flex-col justify-between">
                            <div className="w-16 h-3 bg-[#222] rounded opacity-40" />
                            <div className="w-24 h-6 bg-[#222] rounded opacity-50" />
                        </div>
                        {/* Socials Grid */}
                        <div className="flex-grow grid grid-cols-2 gap-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="bg-[#111] rounded-3xl border border-[#222] animate-pulse min-h-[100px]" />
                            ))}
                        </div>
                    </div>

                    {/* Ticker */}
                    <div className="col-span-1 md:col-span-2 h-[64px] bg-[#111] rounded-3xl border border-[#222] animate-pulse flex items-center px-6 gap-4">
                        <div className="w-full h-8 bg-[#222] rounded opacity-20" />
                    </div>

                    {/* Ads */}
                    <div className="col-span-1 h-[64px] bg-[#111] rounded-3xl border border-[#222] animate-pulse flex items-center px-6 justify-between">
                        <div className="flex flex-col gap-2">
                            <div className="w-20 h-4 bg-[#222] rounded opacity-40" />
                            <div className="w-12 h-2 bg-[#222] rounded opacity-30" />
                        </div>
                        <div className="w-10 h-4 bg-[#222] rounded opacity-40" />
                    </div>
                </div>
            ) : (
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mx-auto h-auto"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    {/* 1. HERO CARD - Minimalist Typo Poster */}
                    <motion.div variants={itemVariants} className={`col-span-1 md:col-span-2 bg-[var(--card-bg)] ${radiusClass} ${borderClass} relative overflow-hidden flex flex-col justify-center items-center py-8 md:p-8 peer/hero group/hero hover:border-[var(--accent)] transition-all duration-500 min-h-[360px]`}>

                        {/* Center Content - Typographic Identity */}
                        <div className="relative z-10 flex flex-col items-center justify-center text-center w-full">

                            {/* "HAMZA" - Balanced & Gradient - Reduced Size to fix clipping */}
                            <motion.h1
                                className="text-[80px] md:text-[100px] leading-[0.85] font-['Outfit'] font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-[#3B82F6] to-[#2A2A2A] select-none filter drop-shadow-2xl px-4"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, ease: "circOut" }}
                            >
                                HAMZA
                            </motion.h1>

                            {/* Refined Description */}
                            <motion.h2
                                className="mt-6 text-lg md:text-xl font-light text-[#888] max-w-md"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                            >
                                Building <span className="font-medium text-white group-hover/hero:text-[var(--accent)] transition-colors">meaningful</span> digital experiences.
                            </motion.h2>
                        </div>
                    </motion.div>

                    {/* 2. RIGHT COLUMN */}
                    <motion.div variants={itemVariants} className="col-span-1 flex flex-col gap-4 h-full">

                        {/* Featured Teaser */}
                        <div
                            className={`h-[150px] bg-[#0F0F0F] ${radiusClass} ${borderClass} p-6 flex flex-col justify-between relative overflow-hidden group cursor-pointer hover:border-blue-500 transition-all duration-300`}
                            onClick={() => navigate('/projects')}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="relative z-20">
                                <span className="text-[10px] text-blue-500 font-mono uppercase tracking-widest block mb-1">{data.featured.label}</span>
                                <h3 className="text-xl font-bold text-white leading-tight">{data.featured.title}</h3>
                            </div>

                            <div className="relative z-20 flex justify-end items-end">
                                <span className="text-[13px] font-bold text-[#666] group-hover:text-blue-500 transition-colors group-hover:underline underline-offset-4">
                                    İncele
                                </span>
                            </div>
                        </div>

                        {/* Socials */}
                        <div className="flex-grow grid grid-cols-2 gap-4">
                            {data.socials.map((social, i) => {
                                if (social.platform === 'contact') {
                                    return (
                                        <button
                                            key={i}
                                            onClick={openContactModal}
                                            className="w-full h-full min-h-[100px] md:min-h-0 flex items-center justify-center rounded-3xl bg-[var(--card-bg)] text-emerald-500 border-2 border-emerald-500 transition-all duration-300 hover:border-[#333] hover:text-[#666]"
                                        >
                                            {getIcon(social.platform)}
                                        </button>
                                    );
                                }
                                return (
                                    <a
                                        key={i}
                                        href={social.url}
                                        target="_blank"
                                        className={`w-full h-full min-h-[100px] md:min-h-0 flex items-center justify-center rounded-3xl bg-[var(--card-bg)] text-[#666] ${getHoverClass(social.platform)} border-2 border-[#2A2A2A] transition-all duration-300`}
                                    >
                                        {getIcon(social.platform)}
                                    </a>
                                );
                            })}
                        </div>
                    </motion.div>

                    {/* 3. BRAND TICKER - Synced Interaction (Seamless Loop) */}
                    <motion.div variants={itemVariants} className={`col-span-1 md:col-span-2 h-[64px] bg-[var(--card-bg)] ${radiusClass} ${borderClass} relative overflow-hidden flex items-center hover:border-[var(--accent)] transition-all duration-300 group/ticker`}>
                        <motion.div
                            className="flex whitespace-nowrap absolute"
                            animate={{ x: "-50%" }}
                            initial={{ x: "0%" }}
                            transition={{ ease: "linear", duration: 60, repeat: Infinity }}
                        >
                            {/* Render Twice for Seamless Loop */}
                            {[...Array(20).fill(data.ticker), ...Array(20).fill(data.ticker)].map((text, i) => (
                                <span key={i} className="text-3xl font-black text-[var(--accent)] opacity-10 mr-8 tracking-tighter peer-hover/hero:opacity-100 group-hover/ticker:opacity-100 transition-opacity duration-300 cursor-default">
                                    {text}
                                </span>
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* 4. ADS */}
                    <motion.a
                        variants={itemVariants}
                        href={data.ads.url}
                        target="_blank"
                        className={`col-span-1 h-[64px] bg-[#0A0A0A] ${radiusClass} ${borderClass} flex items-center justify-between px-6 group cursor-pointer hover:border-blue-500 transition-all`}
                    >
                        <div className="flex flex-col">
                            <span className="font-bold text-white text-sm group-hover:text-blue-500 transition-colors">{data.ads.title}</span>
                            <span className="text-[10px] text-[var(--text-muted)] group-hover:text-[#888]">{data.ads.subtitle}</span>
                        </div>
                        <div className="text-[11px] font-bold text-[#444] group-hover:text-blue-500 transition-colors group-hover:underline underline-offset-2">
                            İncele
                        </div>
                    </motion.a>
                </motion.div>
            )
            }
        </motion.div >
    );
}
